import BasePlayer from './base';

import MediaElement from '../flash/MediaElement';

import { get, createElement, toArray, find, first, isArray } from '../utils';

import type { Player$Options } from '../types/Options';

const AUTO_LEVEL = { name: 'Auto', index: -1 };

export default class FlashPlayer extends BasePlayer {
   $el: HTMLAudioElement | HTMLVideoElement
   _levels = [];
   _currentLevel = null;

   mediaElement: ?MediaElement = null

   constructor(options: Player$Options, state) {
      super(options, state);
      this.logger.debug('flash:controller:creating');

      this.$el = createElement(options.isAudio ? 'audio' : 'video');

      this.onFullScreenChanged = this.onFullScreenChanged.bind(this);

      this.logger.debug('flash:controller:created');
   }

   load({src}) {

      if ( isArray(src) ) {
         this._levels = src;
         this._currentLevel = find(this._levels, lvl => lvl.default) || first(this._levels);
         src = this._currentLevel.src;
      }

      this.logger.debug('flash:controller:loading', src);

      const mediaElement = new MediaElement(this.$el, this.options);
      this.mediaElement = mediaElement;
      mediaElement.setSrc(src);
      mediaElement.setSize('100%', '100%');

      mediaElement.play();

      this.bus.once('loadedmetadata', () => {
         mediaElement.pause();

         if (this.options.qualityLevel) {
            const h = parseInt(this.options.qualityLevel, 10);
            const level = find(this.getLevels(), l => l.height === h);
            if (level) {
               this.setLevel(level);
               this.bus.emit('qualitychange', `${h}p`);
            }
         }
      });

      if (this.options.isAudio) {
         mediaElement.pause();
         this.bus.once('canplaythrough', () => {
            mediaElement.pause();
            this.bus.emit('loadedmetadata');
         });
      }

      this.bus.on('fullscreenchanged', this.onFullScreenChanged);
      this.bus.on('cclayoutchange', this.onFullScreenChanged);
   }

   isHLS() {
      return get(this.mediaElement, 'rendererName') === 'flash_hls';
   }

   getLevels() {
      if (this.isHLS()) {
         const levels = toArray(this.mediaElement.levels);
         return [ AUTO_LEVEL ].concat(levels);
      }
      return this._levels;
   }

   setLevel(level) {
      if (this.isHLS()) {
         this.mediaElement.level = level.index;
      } else if ( this._currentLevel !== level ) {
         const currentTime = this.mediaElement.currentTime;
         const paused = this.mediaElement.paused;
         this._currentLevel = level;
         this.mediaElement.setSrc(level.src);
         this.mediaElement.setSize('100%', '100%');
         this.mediaElement.play();
         this.bus.once('loadedmetadata', () => {
            this.mediaElement.currentTime = currentTime;
            this.mediaElement.pause();
            this.mediaElement.play();
            if (paused) {
               this.mediaElement.pause();
            }
         });
      }
   }

   getLevel() {
      if (this.isHLS()) {
         return this.mediaElement.levels[this.getElement().currentLevel] || AUTO_LEVEL;
      }
      return this._currentLevel;
   }

   getElement() {
      return this.mediaElement;
   }

   setCurrentTime(time: number, silent: ?boolean = true) {
      const paused = this.isPaused();
      this.pause(silent);
      super.setCurrentTime(time, silent);
      if ( !paused ) {
         this.play(silent);
      }
   }

   isMultiQuality() {
      const levels = this.isHLS() ? this.mediaElement.levels : this._levels;
      // eslint-disable-next-line no-magic-numbers
      return levels.length > 1;
   }

   onFullScreenChanged(isFullScreen: ?boolean = false) {
      if (isFullScreen) {
         const { mediaElement } = this;
         if (mediaElement) {
            mediaElement.setSize('100%', '100%');
         }
      } else {
         setTimeout(() => this._resizeME());
      }
   }

   _resizeME() {
      const parent = this.$el.parentNode;
      const me = this.mediaElement;
      if (parent && me && parent instanceof HTMLElement) {
         const width = parent.offsetWidth;
         const height = parent.offsetHeight;
         me.setSize(`${width}px`, `${height}px`);
      }
   }

   getBufferedPercent() {
      const $el = this.mediaElement;
      return $el.buffered.end($el.buffered.length - 1) / $el.duration;
   }

   destroy() {
      this.pause();
      const { mediaElement } = this;
      if ( mediaElement ) {
         mediaElement.renderer.hide();
      }
      super.destroy();
      this.bus.off('fullscreenchanged', this.onFullScreenChanged);
      this.bus.off('cclayoutchange', this.onFullScreenChanged);

      if (mediaElement) {
         mediaElement.removeChild(this.$el);
         mediaElement.parentNode.removeChild(mediaElement);
      }
      this.mediaElement = null;
   }
}
